// WHAT: Horizontal bar chart component for displaying time by aspect
// WHY:  Provides visual breakdown of time spent per aspect with workspace filtering
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: recharts for charting, useFilter, useWorkspaceConfig hooks
// RISKS:
//   - NOTE: Color mapping depends on workspace config - may not match if config is missing
//   - NOTE: Workspace filtering may not work correctly if aspect labels don't match format
//   - NOTE: Chart may not render correctly with many aspects

import type { WorkspaceConfig } from "@/shared";
import type { JSX } from "react";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

import { Card } from "@/components/ui";
import { useFilter } from "@/domains/productivity/timetracq/components/molecules/FilterContextProvider.shared";
import { useWorkspaceConfig } from "@/domains/productivity/timetracq/hooks/time_tracking/useWorkspaceConfig";
import { getSafeAspectColor } from "@/shared";

type UseWorkspaceConfigResult = [WorkspaceConfig, (cfg: WorkspaceConfig) => void, boolean];

const safeUseFilter = useFilter as () => unknown;
const useSafeWorkspaceConfig = useWorkspaceConfig as () => UseWorkspaceConfigResult;

interface FilterLike {
  dateFilter: unknown;
  period: string;
}

const isFilterLike = (value: unknown): value is FilterLike => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record.period === "string" && "dateFilter" in record;
};

/**
 * Aspect entry interface
 */
interface AspectEntry {
  /** Aspect label (workspace/aspect format) */
  label: string;
  /** Minutes */
  minutes: number;
}

/**
 * Backend response interface
 */
interface BackendResponse {
  /** Category chart data */
  category_chart: AspectEntry[];
}

/**
 * WHAT:
 *   Horizontal bar chart component that displays time spent per aspect with
 *   workspace filtering and color coding based on workspace configuration.
 *
 * WHY:
 *   Enables users to visualize time distribution across aspects with workspace
 *   filtering capabilities.
 *
 * BEHAVIOR:
 *   - Fetches aggregated data from backend
 *   - Filters by selected workspace
 *   - Displays horizontal bar chart
 *   - Colors bars based on workspace config
 *   - Updates when filter or workspace selection changes
 *
 * RISKS:
 *   - NOTE: Color mapping depends on workspace config - may not match if config is missing
 *   - NOTE: Workspace filtering may not work correctly if aspect labels don't match format
 *   - NOTE: Chart may not render correctly with many aspects
 *
 * @returns JSX.Element
 *
 */
export const AspectBarChart = () => {
  const rawFilter = safeUseFilter();
  const { dateFilter, period } = isFilterLike(rawFilter)
    ? rawFilter
    : { dateFilter: undefined, period: "All" };
  const [cfg, , cfgLoading] = useSafeWorkspaceConfig();

  const [data, setData] = useState<AspectEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>("All");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await core.invoke<BackendResponse>("get_aggregated_summary", {
          filter: dateFilter,
        });
        setData(res.category_chart);
      } catch {
        setError("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [dateFilter]);

  // Workspace-Optionen für Filter (ohne Meta-Keys wie "Settings")
  const workspaceOptions = useMemo(() => {
    if (cfgLoading || typeof cfg !== "object" || Object.keys(cfg).length === 0) {
      return [];
    }

    const metaKeys = new Set(["settings", "Meta", "System", "Version"]);
    const options = [{ label: "All Workspaces", value: "All" }];

    for (const workspace of Object.keys(cfg)) {
      // Filtere Meta-Keys aus
      if (!metaKeys.has(workspace)) {
        options.push({ label: workspace, value: workspace });
      }
    }

    return options;
  }, [cfg, cfgLoading]);

  // Gefilterte Daten basierend auf ausgewähltem Workspace
  const filteredData = useMemo(() => {
    if (selectedWorkspace === "All") {
      return data;
    }
    if (typeof cfg !== "object" || Object.keys(cfg).length === 0) {
      return [];
    }

    return data.filter((entry) => {
      // Prüfe, ob dieser Aspect zum ausgewählten Workspace gehört
      for (const ws of Object.keys(cfg)) {
        if (ws === selectedWorkspace) {
          const color = getSafeAspectColor(cfg, ws, entry.label);
          if (color) {
            return true;
          }
        }
      }
      return false;
    });
  }, [data, selectedWorkspace, cfg]);

  // Aggregiere Daten pro Aspect (falls Duplikate vorhanden sind)
  const aggregatedData = useMemo(() => {
    const aspectMap = new Map<string, number>();

    for (const entry of filteredData) {
      const existing = aspectMap.get(entry.label) ?? 0;
      aspectMap.set(entry.label, existing + entry.minutes);
    }

    const result = [...aspectMap.entries()].map(([label, minutes]) => ({
      label,
      minutes,
    }));

    return result;
  }, [filteredData]);

  const coloredData = useMemo(() => {
    if (cfgLoading || typeof cfg !== "object" || Object.keys(cfg).length === 0) {
      return aggregatedData.map((d) => ({ ...d, color: "#888888" }));
    }

    return aggregatedData.map((d) => {
      let color: string | undefined;

      if (selectedWorkspace === "All") {
        // Bei "All Workspaces" verwende die Farbe des ersten Workspaces, der diesen Aspect hat
        for (const ws of Object.keys(cfg)) {
          color = getSafeAspectColor(cfg, ws, d.label);
          if (color) {
            break;
          }
        }
      } else {
        // Bei spezifischem Workspace verwende nur die Farbe dieses Workspaces
        color = getSafeAspectColor(cfg, selectedWorkspace, d.label);
      }

      return { ...d, color: color ?? "#888888" };
    });
  }, [aggregatedData, cfg, cfgLoading, selectedWorkspace]);

  /**
   * WHAT:
   *   Returns chart content based on loading, error, or data state.
   *
   * @returns JSX.Element for chart content
   *
   */
  const getChartContent = (): JSX.Element => {
    if (loading || cfgLoading) {
      return (
        <div className="h-40 flex items-center justify-center text-slate-500 animate-pulse">
          Loading…
        </div>
      );
    }

    if (error) {
      return <div className="h-40 flex items-center justify-center text-red-400">{error}</div>;
    }

    if (coloredData.length === 0) {
      const emptyMessage =
        selectedWorkspace === "All"
          ? "Keine Daten für den gewählten Zeitraum"
          : `Keine Aspects für "${selectedWorkspace}" im gewählten Zeitraum`;
      return (
        <div className="h-40 flex items-center justify-center text-slate-300">{emptyMessage}</div>
      );
    }

    return (
      <ResponsiveContainer height={240} width="100%">
        <BarChart
          data={coloredData}
          layout="vertical"
          margin={{ bottom: 10, left: 20, right: 20, top: 10 }}
        >
          <XAxis hide type="number" />
          <YAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 12 }} type="category" />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1E293B",
              border: "1px solid #334155",
              borderRadius: "8px",
              color: "#F1F5F9",
            }}
            formatter={(v: number) => `${String(Math.round(v / 60))} h ${String(v % 60)} m`}
          />
          <Bar dataKey="minutes" fill="#888888" isAnimationActive={false}>
            {coloredData.map((entry) => (
              <Cell fill={entry.color} key={`cell-${entry.label}`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card
      className="bg-slate-800/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">Aspects ({period})</div>

          {/* Workspace Filter */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">Workspace:</label>
            <select
              className="bg-slate-700/50 border border-slate-500/50 text-slate-200 text-xs px-2 py-1 rounded-md focus:ring-2 focus:ring-tracq-bright-blue focus:border-tracq-bright-blue transition-all duration-200"
              onChange={(e) => {
                setSelectedWorkspace(e.target.value);
              }}
              value={selectedWorkspace}
            >
              {workspaceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {getChartContent()}
      </div>
    </Card>
  );
};
